import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ARTICLES_URL = 'https://news-writer.itsmybadsoul.workers.dev/articles.json';
const FALLBACK_IMAGE = '/images/news/market.jpg';
const SEEN_KEY = 'tad_news_seen_slug';
const AUTO_KEY = 'tad_news_auto_opened';

const AR_MONTHS = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

const TYPE_LABELS: Record<string, string> = {
    close: 'تحليل الجلسة',
    summary: 'ملخص الجلسة',
    liquidity: 'خريطة السيولة',
    premarket: 'قبل الافتتاح',
    news: 'أخبار الشركات',
};

interface PulseItem {
    slug: string;
    title: string;
    image: string;
    date: string;
    type: string;
    isNew: boolean;
}

// يوم/شهر تقويم الرياض من غير ما نرمّج تاريخ ونعيد قراءته — التحويل ده بيفشل في متصفحات فتبقى
// «اليوم/أمس» غلط لزوار بتوقيت مختلف.
const riyadhDay = (d: Date) => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Riyadh', year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(d);
    const value = (type: string) => Number(parts.find((p) => p.type === type)?.value || 0);
    return { index: Date.UTC(value('year'), value('month') - 1, value('day')), day: value('day'), month: value('month') };
};

const formatDate = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const target = riyadhDay(d);
    const today = riyadhDay(new Date());
    const diff = Math.round((today.index - target.index) / (24 * 60 * 60 * 1000));
    if (diff === 0) return 'اليوم';
    if (diff === 1) return 'أمس';
    return `${target.day} ${AR_MONTHS[target.month - 1]}`;
};

const NewsPulse: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [items, setItems] = useState<PulseItem[]>([]);
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [unseen, setUnseen] = useState(0);
    const isArabic = String(i18n.language || 'ar').startsWith('ar');

    useEffect(() => {
        let cancelled = false;
        fetch(`${ARTICLES_URL}?t=${Date.now()}`)
            .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
            .then((data) => {
                if (cancelled) return;
                const raw = Array.isArray(data?.articles) ? data.articles : [];
                const sorted = raw
                    .filter((a: any) => a?.slug && a?.title)
                    .sort((a: any, b: any) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime())
                    .slice(0, 5);

                let seen = '';
                try { seen = localStorage.getItem(SEEN_KEY) || ''; } catch { /* private mode */ }
                const seenIndex = sorted.findIndex((a: any) => a.slug === seen);

                setItems(sorted.map((a: any, i: number) => ({
                    slug: String(a.slug),
                    title: String(a.title),
                    image: String(a.image || FALLBACK_IMAGE),
                    date: String(a.date || ''),
                    type: String(a.type || ''),
                    isNew: seen ? (seenIndex === -1 ? i < 1 : i < seenIndex) : i < 1,
                })));
                setUnseen(seen ? (seenIndex === -1 ? 1 : seenIndex) : (sorted.length ? 1 : 0));
                setLoaded(true);

                let autoDone = '';
                try { autoDone = localStorage.getItem(AUTO_KEY) || ''; } catch { /* private mode */ }
                if (!autoDone) {
                    window.setTimeout(() => setOpen(true), 2400);
                }
            })
            .catch(() => { if (!cancelled) setLoaded(true); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        window.dispatchEvent(new CustomEvent('tad-news-panel', { detail: { open } }));
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const newest = items[0]?.slug;
        try {
            if (newest) localStorage.setItem(SEEN_KEY, newest);
            localStorage.setItem(AUTO_KEY, '1');
        } catch { /* private mode */ }
        setUnseen(0);
        const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, items]);

    if (!loaded || !items.length) return null;

    return (
        <>
            {/* الشريط الجانبي المطوي */}
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label={t('newsPulseTitle')}
                aria-expanded={open}
                className={`fixed z-[55] left-0 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5 sm:gap-2 py-3 px-1.5 sm:py-4 sm:px-2 rounded-r-xl sm:rounded-r-2xl border border-l-0 border-purple-400/30 bg-gradient-to-b from-purple-700/90 to-indigo-700/90 text-white shadow-[0_0_24px_-6px_rgba(139,92,246,0.8)] sm:shadow-[0_0_30px_-8px_rgba(139,92,246,0.8)] hover:from-purple-600 hover:to-indigo-600 transition-all duration-300 ${open ? 'opacity-0 pointer-events-none -translate-x-full' : 'opacity-100'}`}
            >
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 5.5A1.5 1.5 0 015.5 4h9.586a1.5 1.5 0 011.06.44l3.915 3.914a1.5 1.5 0 01.439 1.06V18.5A1.5 1.5 0 0119 20H5.5A1.5 1.5 0 014 18.5v-13z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h7M8 14h5" />
                </svg>
                <span className="news-rail text-[9px] sm:text-[10px] font-bold">{t('newsPulseTitle')}</span>
                {unseen > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 news-pulse min-w-[16px] h-4 sm:min-w-[18px] sm:h-[18px] px-1 rounded-full bg-rose-500 text-[9px] sm:text-[10px] font-bold flex items-center justify-center">
                        {unseen}
                    </span>
                )}
            </button>

            {/* اللوحة المفتوحة */}
            <aside
                role="dialog"
                aria-label={t('newsPulseTitle')}
                className={`fixed z-[56] left-0 top-1/2 -translate-y-1/2 w-[350px] max-w-[92vw] transition-all duration-500 ease-out max-sm:top-20 max-sm:translate-y-0 max-sm:z-[61] ${open ? 'opacity-100 translate-x-0 pointer-events-auto' : 'opacity-0 -translate-x-[105%] pointer-events-none'}`}
            >
                <div className="glass-card panel-solid rounded-r-3xl rounded-l-none max-sm:rounded-3xl max-sm:mx-2 overflow-hidden border-l-0 max-sm:border-l">
                    <div className="relative px-4 pt-4 pb-3 border-b border-white/10 bg-gradient-to-l from-purple-700/25 to-transparent">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                                    </span>
                                    <h3 className="text-white font-bold text-sm">{t('newsPulseTitle')}</h3>
                                    <span className="neon-chip">AI</span>
                                </div>
                                <p className="text-[11px] text-gray-400 mt-1">{t('newsPulseSubtitle')}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                aria-label="إغلاق"
                                className="text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-full p-1.5 transition-colors"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <ul className="max-h-[58vh] max-sm:max-h-[42vh] overflow-y-auto divide-y divide-white/5">
                        {items.map((item) => (
                            <li key={item.slug}>
                                <a
                                    href={`/news/${item.slug}/`}
                                    className="group flex gap-3 p-3 hover:bg-white/[0.04] transition-colors"
                                >
                                    <img
                                        src={item.image}
                                        alt=""
                                        loading="lazy"
                                        className="w-16 h-14 rounded-xl object-cover border border-white/10 flex-shrink-0"
                                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = FALLBACK_IMAGE; }}
                                    />
                                    <div className="min-w-0">
                                        <p className="text-[13px] leading-snug font-semibold text-white/90 group-hover:text-white line-clamp-2">
                                            {item.title}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1.5 text-[10px] text-gray-400">
                                            {item.isNew && <span className="text-rose-400 font-bold">{t('newsPulseNew')}</span>}
                                            <span>{formatDate(item.date)}</span>
                                            {isArabic && TYPE_LABELS[item.type] && (
                                                <span className="text-purple-300/80">· {TYPE_LABELS[item.type]}</span>
                                            )}
                                        </div>
                                    </div>
                                </a>
                            </li>
                        ))}
                    </ul>

                    <div className="px-4 py-3 border-t border-white/10 bg-white/[0.02]">
                        <a href="/news/" className="flex items-center justify-between text-xs font-bold text-purple-300 hover:text-purple-200 transition-colors">
                            <span>{t('newsPulseAll')}</span>
                            <span aria-hidden="true">←</span>
                        </a>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default NewsPulse;
